/**
 * Skateboard stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const skateboardStickersPack: ToilePack = {
    slug: 'skateboard-stickers',
    category: 'sticker',
    name: 'Skateboard stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'board', label: 'Skateboard', subject: 'a skateboard seen from below with its wheels'},
        {key: 'wheel', label: 'Flaming wheel', subject: 'a skateboard wheel with flames trailing'},
        {key: 'badge', label: 'Skate badge', subject: 'a round skate badge with a lightning bolt through the centre'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a longboard instead', 'for a laptop lid', 'in checkerboard pattern'],
    directionHint: 'try: a longboard instead',
    seo: {
        title: 'Skateboard Stickers: Deck, Flaming Wheel, Badge',
        metaDescription: 'Nine skateboard stickers — a deck from below, a flaming wheel, a lettered skate badge — in retro, bold cartoon, and minimal line styles.',
        h1: 'Skateboard stickers: deck, flaming wheel, skate badge',
        tagline: 'Nine stickers, three skate icons, three styles each.',
        intro: 'A skateboard seen from below with its wheels, a wheel trailing flames, and a round skate badge with a lightning bolt through the centre — three skate stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a longboard instead, a skate-park scene, or a helmet composition, and the same three styles carry it.',
        useCases: [
            'a skateboard deck or helmet',
            'a laptop or water bottle',
            'a phone case sticker sheet',
            'a skate-park bag or bumper',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine skateboard stickers — a deck, a flaming wheel, and a lettered skate badge — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a longboard or a skate-park scene?', a: 'Yes — the direction field takes any variation, including a longboard instead, a skate-park scene, or a helmet composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a deck-sized print.'},
            {q: 'Does the badge carry a real brand name?', a: "No — the lettering is generic ('badge' style), not a real skate brand or logo."},
        ],
    },
}
